// Shared mapping: global user roles -> team member role (mirrors backend _derive_team_member_role).
// Returns null when none of the roles allows a team membership (Fan, Eltern, Dienstplaner, ...).
//
// Uses an ALLOWLIST of known aliases with word-boundary semantics instead of a loose
// substring match. This prevents unrelated custom roles from being misclassified
// (e.g. "Physio-Trainer" should NOT be recognised as a plain "Trainer").

const TRAINER_ALIASES = new Set([
    'trainer', 'trainerin', 'trainer*in', 'trainer/in',
    'coach', 'cheftrainer', 'chef-trainer', 'haupttrainer', 'haupt-trainer',
    'übungsleiter', 'uebungsleiter',
    'torwarttrainer', 'torwart-trainer',
    'athletiktrainer', 'athletik-trainer',
]);
const CO_TRAINER_ALIASES = new Set([
    'co-trainer', 'cotrainer', 'co trainer', 'co-trainerin', 'cotrainerin',
    'assistenztrainer', 'assistenz-trainer', 'assistant-coach', 'assistant coach',
]);
const BETREUER_ALIASES = new Set([
    'betreuer', 'betreuerin', 'betreuer*in', 'betreuer/in',
    'betreuung', 'teambetreuer', 'team-betreuer',
]);
const TEAMMANAGER_ALIASES = new Set([
    'teammanager', 'team-manager', 'team manager', 'manager',
]);
const PHYSIO_ALIASES = new Set([
    'physio', 'physiotherapeut', 'physiotherapeutin', 'physiotherapeut*in',
]);
const VERANTWORTLICHER_ALIASES = new Set([
    'verantwortlicher', 'verantwortliche', 'verantwortliche*r',
    'teamverantwortlicher', 'team-verantwortlicher', 'teamverantwortliche',
]);
const SPIELER_ALIASES = new Set([
    'spieler', 'spielerin', 'spieler*in', 'spieler/in',
    'ersatzspieler', 'ersatzspielerin', 'ersatzspieler*in',
    'player', 'member',
]);

// Admin-configurable aliases. Populated at app boot via setCustomTeamRoleAliases(),
// which reads settings.custom_team_role_mappings from GET /api/settings/team-role-mappings.
let _customAliasMap = {}; // { alias_lowercase: team_role }

export const setCustomTeamRoleAliases = (mappings) => {
    _customAliasMap = {};
    for (const m of (mappings || [])) {
        const alias = (m?.alias || '').toLowerCase().trim();
        const teamRole = (m?.team_role || '').trim();
        if (!alias || !teamRole) continue;
        _customAliasMap[alias] = teamRole;
    }
};

export const getCustomTeamRoleAliases = () => ({ ..._customAliasMap });

const _normalize = (r) => String(r || '').toLowerCase().trim();

export const deriveTeamMemberRole = (userRoles) => {
    const normalized = (userRoles || []).filter(Boolean).map(_normalize);
    // Priority order: Co-Trainer > Trainer > Betreuer > Teammanager > Physio > Verantwortlicher > Spieler
    if (normalized.some(n => CO_TRAINER_ALIASES.has(n))) return 'Co-Trainer';
    if (normalized.some(n => TRAINER_ALIASES.has(n))) return 'Trainer';
    if (normalized.some(n => BETREUER_ALIASES.has(n))) return 'Betreuer';
    if (normalized.some(n => TEAMMANAGER_ALIASES.has(n))) return 'Teammanager';
    if (normalized.some(n => PHYSIO_ALIASES.has(n))) return 'Physio';
    if (normalized.some(n => VERANTWORTLICHER_ALIASES.has(n))) return 'Verantwortlicher';
    if (normalized.some(n => SPIELER_ALIASES.has(n))) return 'Spieler';
    // Admin-configured custom aliases fall back
    for (const n of normalized) {
        if (_customAliasMap[n]) return _customAliasMap[n];
    }
    return null;
};
